import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const authApi = axios.create({ baseURL: API, withCredentials: true });

// Silently refresh an expired access token once, then retry the original request.
authApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retried && !original.url.includes("/auth/")) {
      original._retried = true;
      try {
        await authApi.post("/auth/refresh");
        return authApi(original);
      } catch (_) {
        /* fall through */
      }
    }
    return Promise.reject(error);
  }
);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AuthContext = createContext({ user: null, login: async () => {}, logout: async () => {} });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = signed out

  useEffect(() => {
    authApi
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(async () => {
        try {
          const r = await authApi.post("/auth/refresh");
          setUser(r.data);
        } catch (_) {
          setUser(false);
        }
      });
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await authApi.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await authApi.post("/auth/logout");
    setUser(false);
  }, []);

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
